/**
 * Shared backdrop for every out-of-game screen: the staff map on the war-room table at night.
 * Layers (bottom → top): procedural Canvas2D map (fallback, drifts slowly) · PixiJS attract
 * renderer (covers the fallback once it draws) · night tint + lamp pool + vignette · content.
 */
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useT } from '../../i18n';
import { createMapRenderer, type MapRendererHandle } from '../../render';
import s from './menu.module.css';
import { paintStaffMap } from './staffMap';

function MapBackdrop({ seed }: { seed: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const regions = useT()('map.backdrop.regions');
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let timer = 0;
    let alive = true;
    const paint = () => {
      if (!alive) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintStaffMap(ctx, w, h, seed, regions.split('|'));
      canvas.dataset.ready = 'true';
    };
    // Map lettering uses EB Garamond: make sure the faces are loaded before painting.
    Promise.all(
      [
        '600 17px "EB Garamond"',
        '500 15px "EB Garamond"',
        'italic 13px "EB Garamond"',
        '600 7px "IBM Plex Sans Condensed"',
      ].map((f) => document.fonts.load(f).catch(() => [])),
    ).then(paint);
    const onResize = () => {
      clearTimeout(timer);
      timer = window.setTimeout(paint, 180);
    };
    window.addEventListener('resize', onResize);
    return () => {
      alive = false;
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [seed, regions]);
  return <canvas ref={ref} className={s.canvas} />;
}

export function MenuShell({ children }: { children: ReactNode }) {
  const seed = useMemo(() => Math.floor(Math.random() * 0x7fffffff), []);
  const attractRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = attractRef.current;
    if (!el) return;
    let handle: MapRendererHandle | null = null;
    let cancelled = false;
    createMapRenderer(el, { attract: true, seed })
      .then((h) => {
        if (cancelled) h.destroy();
        else handle = h;
      })
      .catch((err: unknown) =>
        console.warn('[menu] attract renderer unavailable, using painted backdrop', err),
      );
    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [seed]);

  return (
    <div className={s.shell}>
      <div className={s.table}>
        <div className={s.drift}>
          <MapBackdrop seed={seed} />
        </div>
      </div>
      <div ref={attractRef} className={s.attract} />
      <div className={s.night} />
      <div className={s.lamp} />
      <div className={s.content}>{children}</div>
    </div>
  );
}
